import {EmojiButton} from '../../emoji-button/index.js';
import DomCreate from '../utils/domCreate.js';

export default class Editor {
    constructor(textarea_element, csrf_token) {
        this.textarea_element = textarea_element;
        this.csrf_token = csrf_token;

        this.md_tokens = {
            bold: { start: '**', middle: '', end: '**', label: 'Tučné', icon: 'bold', custom: false },
            italic: { start: '*', middle: '', end: '*', label: 'Kurzíva', icon: 'italic', custom: false },
            link: { start: '[', middle: '](', end: ')', label: 'Odkaz', icon: 'link', custom: true },
            email: { start: '<mailto:', middle: '', end: '>', label: 'E-mail', icon: 'envelope', custom: true },
            phone: { start: '<tel:', middle: '', end: '>', label: 'Telefon', icon: 'phone', custom: true },
        }

        this.buttons = {}

        this.toolbar = null;
        this.active_token = null;
        this.active_token_positions = null;

        this.emoji_picker = new EmojiButton();

        this.initToolbar();
        this.initSelectHandler();
    }

    initToolbar() {
        Object.keys(this.md_tokens).forEach((key) => {
            if (this.md_tokens[key].custom) {
                return;
            }
            const value = this.md_tokens[key];
            this.buttons[key] = this._createButtonWithInline(key, value.icon, value.label, value.start, value.middle + value.end);
        });
        this.buttons['link'] = this._createLinkButton('link', this.md_tokens.link);
        this.buttons['phone'] = this._createInputButton('phone', this.md_tokens.phone);
        this.buttons['email'] = this._createInputButton('email', this.md_tokens.email);

        this.toolbar = this._createToolbar(
            [
                [
                    this.buttons.bold,
                    this.buttons.italic,
                ],
                [
                    this.buttons.link,
                    this.buttons.email,
                    this.buttons.phone,
                ],
                [
                    this._createEmojiButton('smile', 'emoji'),
                ],
                [
                    this._createPreviewButton('eye', 'ukázat'),
                ],
            ]
        );
        this.textarea_element.parentNode.insertBefore(this.toolbar, this.textarea_element);
    }

    initSelectHandler() {
        this.textarea_element.addEventListener('select', () => {this._handleSelectEvent();});
        this.textarea_element.addEventListener('keyup', () => {this._handleSelectEvent();});
        this.textarea_element.addEventListener('mouseup', () => {this._handleSelectEvent();});
    }

    _createToolbar(columns) {
        const toolbar_element = this._createElement("div",
            ["toolbar", "columns", "is-mobile", "p-1", "has-background-light"]);

        columns.forEach(column => {
            const column_element = document.createElement("div");
            column_element.classList.add("column");
            column_element.classList.add("is-narrow");
            column.forEach(function (element) {
                column_element.insertBefore(element, null);
            })
            toolbar_element.insertBefore(column_element, null);
        });
        const last_column_element = document.createElement("div");
        last_column_element.classList.add("column");
        toolbar_element.insertBefore(last_column_element, null);

        return toolbar_element;

    }

    _createButton(icon, tooltip, classes = []) {
        const button = document.createElement("button");
        button.classList.add("button");
        button.classList.add("is-small");
        button.type = "button";
        button.dataset.tooltip = tooltip;
        classes.forEach(cls => { button.classList.add(cls); });

        const span = document.createElement("span");
        span.classList.add("icon");

        const i = document.createElement("i");
        i.classList.add("fa-solid");
        i.classList.add(`fa-${icon}`);

        span.insertBefore(i, null);
        button.insertBefore(span, null);

        return button;
    }

    _createButtonWithInline(name, icon, tooltip, pre, post, classes = []) {
        const button = this._createButton(icon, tooltip, classes);

        button.addEventListener("click", event => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            event.preventDefault();

            if (this.active_token === name){
                this._removeActiveToken();
            }else {
                this._insertText(pre, post, tooltip);
            }
            this._handleSelectEvent()
        });

        return button;
    }

    _createPreviewButton(icon, tooltip, classes = []) {
        const button = this._createButton(icon, tooltip, classes);

        const i = button.querySelector('i.fa-solid');
        i.classList.add('htmx-indicator');

        const overlay = this._createOverlayElement();

        button.addEventListener("click", (event) => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            event.preventDefault();

            if (overlay.element.classList.contains('is-hidden')) {
                htmx.ajax('POST', '/md-htmx', {
                    target: overlay.content,
                    source: button,
                    swap: 'innerHTML',
                    values: {
                        csrf_token: this.csrf_token,
                        markdown: this.textarea_element.value,
                    }
                }).then(() => {
                    overlay.element.classList.remove('is-hidden');
                });
            }
        });

        return button;
    }

    _createInputButton(name, md_token, classes = []) {
        const button = this._createButton(md_token.icon, md_token.label, classes);

        const overlay = this._createOverlayElement();

        overlay.content.innerHTML = `
            <div class="field">
              <label class="label is-small">${md_token.label}</label>
              <div class="control">
                <input class="input is-small" type="text" placeholder="${md_token.label}">
              </div>
            </div>
            <div class="buttons">
                <button type="button" class="submit button is-primary is-small">Upravit</button>
                <button type="button" class="cancel button is-small">Zrušit</button>
                <button type="button" class="remove button is-danger is-small">Smazat</button>
            </div>
        `;

        const input = overlay.content.querySelector('input');

        const submit = () => {
            if (this.active_token === name) {
                const currentText = this.textarea_element.value;
                this.textarea_element.value = currentText.substring(0, this.active_token_positions.start)
                    + input.value
                    + currentText.substring(this.active_token_positions.end);
            }else{
                this._replaceSelectedText(md_token.start, md_token.end, input.value);
            }
            overlay.element.classList.add('is-hidden');
            this._handleSelectEvent();
        };

        input.addEventListener("click", event => {event.stopPropagation();});
        input.addEventListener("keydown", event => {
            if (event.key === 'Enter') {
                event.stopPropagation();
                event.preventDefault();
                submit();
            }
        });

        const button_submit = overlay.content.querySelector('button.submit');
        button_submit.addEventListener("click", submit);
        const button_delete = overlay.content.querySelector('button.remove');
        button_delete.addEventListener("click", event => {
            if (this.active_token === name) {
                this._removeActiveToken(true);
            }
            this._handleSelectEvent();
        });

        button.addEventListener("click", event => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            event.preventDefault();

            if (this.active_token === name){
                input.value = this.textarea_element.value.substring(this.active_token_positions.start, this.active_token_positions.end);
                button_delete.classList.remove('is-hidden');
            }else {
                input.value = this._getSelectedText("");
                button_delete.classList.add('is-hidden');
            }
            overlay.element.classList.remove('is-hidden');
            input.focus();
        });

        return button;
    }

    _createLinkButton(name, md_token, classes = []) {
        const button = this._createButton(md_token.icon, md_token.label, classes);

        const overlay = this._createOverlayElement();

        overlay.content.innerHTML = `
            <div class="field">
              <label class="label is-small">${md_token.label}</label>
              <div class="control">
                <input class="input name is-small" type="text" placeholder="Název odkazu">
              </div>
            </div>
            <div class="field">
              <div class="control">
                <input class="input link is-small" type="text" placeholder="${md_token.label}">
              </div>
            </div>
            <div class="buttons">
                <button type="button" class="submit button is-primary is-small">Upravit</button>
                <button type="button" class="cancel button is-small">Zrušit</button>
                <button type="button" class="remove button is-danger is-small">Smazat</button>
            </div>
        `;

        const input_name = overlay.content.querySelector('input.name');
        const input_link = overlay.content.querySelector('input.link');

        const submit = () => {
            if (this.active_token === name) {
                const currentText = this.textarea_element.value;
                this.textarea_element.value = currentText.substring(0, this.active_token_positions.start)
                    + input_name.value + md_token.middle + input_link.value
                    + currentText.substring(this.active_token_positions.end);
            }else{
                this._replaceSelectedText(md_token.start, md_token.end, input_name.value + md_token.middle + input_link.value);
            }
            overlay.element.classList.add('is-hidden');
            this._handleSelectEvent();
        };

        input_name.addEventListener("click", event => {event.stopPropagation();});
        input_name.addEventListener("keydown", event => {
            if (event.key === 'Enter') {
                event.stopPropagation();
                event.preventDefault();
                submit();
            }
        });
        input_link.addEventListener("click", event => {event.stopPropagation();});
        input_link.addEventListener("keydown", event => {
            if (event.key === 'Enter') {
                event.stopPropagation();
                event.preventDefault();
                submit();
            }
        });

        const button_submit = overlay.content.querySelector('button.submit');
        button_submit.addEventListener("click", submit);
        const button_delete = overlay.content.querySelector('button.remove');
        button_delete.addEventListener("click", event => {
            if (this.active_token === name) {
                this._removeActiveToken(true);
            }
            this._handleSelectEvent();
        });

        button.addEventListener("click", event => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            event.preventDefault();

            if (this.active_token === name){
                const inner = this.textarea_element.value.substring(this.active_token_positions.start, this.active_token_positions.end);
                const pieces = inner.split(md_token.middle);
                input_name.value = pieces[0];
                input_link.value = pieces[1];
                button_delete.classList.remove('is-hidden');
            }else {
                input_name.value = this._getSelectedText("");
                input_link.value = 'https://';
                button_delete.classList.add('is-hidden');
            }
            overlay.element.classList.remove('is-hidden');
            input_name.focus();
        });

        return button;
    }

    _createEmojiButton(icon, tooltip, classes = []) {
        const button = this._createButton(icon, tooltip, classes);

        this.emoji_picker.on('emoji', selection => {
            console.log("EMOJI", selection);
            this._replaceSelectedText("", "", selection.emoji);
            this._handleSelectEvent();
        });

        button.addEventListener("click", (event) => {
            this.emoji_picker.togglePicker(button);
        });

        return button;
    }

    _handleSelectEvent() {
        const text = this.textarea_element.value;
        const cursor_position = this.textarea_element.selectionStart;
        let newline_start_position = text.lastIndexOf("\n", cursor_position-1);
        let newline_end_position = text.indexOf("\n", cursor_position);
        if (newline_end_position < 0) { newline_end_position = text.length + 1; }

        const start_positions = [{name: 'newline', position: newline_start_position}];
        const end_positions = [{name: 'newline', position: newline_end_position}];

        Object.keys(this.md_tokens).forEach((key) => {
            const token = this.md_tokens[key];
            let start_position = text.lastIndexOf(token.start, cursor_position-1);
            if (start_position >= 0) { start_position += token.start.length; }
            let end_position = text.indexOf(token.end, cursor_position);
            if (end_position < 0) { end_position = text.length + 1; }
            start_positions.push({name: key, position: start_position});
            end_positions.push({name: key, position: end_position});
        });

        let start_token = 'newline';
        let start_pos = -1;
        let end_token = ['newline'];
        let end_pos = text.length+1;
        let end_chars = '';
        start_positions.forEach(start_position => {
            if (start_pos < start_position.position) {
                start_token = start_position.name;
                start_pos = start_position.position;
            }
        });
        end_positions.forEach(end_position => {
            if (end_pos > end_position.position) {
                end_token = [end_position.name];
                end_chars = (end_position.name !== 'newline')?this.md_tokens[end_position.name]:'';
                end_pos = end_position.position;
            } else {
                if (end_pos === end_position.position) {
                    let new_chars = '';
                    if (end_position.name !== 'newline') {
                        new_chars = this.md_tokens[end_position.name];
                    }
                    if (new_chars.length > end_chars.length) {
                        end_token = [end_position.name];
                        end_chars = new_chars;
                        end_pos = end_position.position;
                    } else if (new_chars.length === end_chars.length) {
                        end_token.push(end_position.name);
                    }
                }
            }
        });

        if (end_token.includes(start_token) && start_token !== 'newline') {
            this._activateButtons(start_token, { start: start_pos, end: end_pos});
        }else{
            this._activateButtons(null, null);
        }
    }

    _activateButtons(activate, positions) {
        this.active_token = null;
        this.active_token_positions = null;
        Object.keys(this.buttons).forEach((key) => {
            if (activate === null) {
                this.buttons[key].classList.remove("is-active");
                this.buttons[key].disabled = false;
                return;
            }
            if (key === activate) {
                this.buttons[key].classList.add("is-active");
                this.buttons[key].disabled = false;
                this.active_token_positions = positions;
                this.active_token = activate;
            } else {
                this.buttons[key].classList.remove("is-active");
                this.buttons[key].disabled = true;
            }
        });
    }

    _getSelectedText(placeholder) {
        const currentSelectionStart = this.textarea_element.selectionStart;
        const currentSelectionEnd = this.textarea_element.selectionEnd;
        const currentText = this.textarea_element.value;

        if (currentSelectionStart !== currentSelectionEnd) {
            return currentText.substring(currentSelectionStart, currentSelectionEnd);
        }
        return placeholder;
    }

    _replaceSelectedText(pre_syntax, post_syntax, text) {
        const currentSelectionStart = this.textarea_element.selectionStart;
        const currentSelectionEnd = this.textarea_element.selectionEnd;
        const currentText = this.textarea_element.value;

        this.textarea_element.value = currentText.substring(0, currentSelectionStart)
            + pre_syntax + text + post_syntax
            + currentText.substring(currentSelectionEnd);

        this.textarea_element.focus();
        this.textarea_element.selectionStart = currentSelectionStart + pre_syntax.length;
        this.textarea_element.selectionEnd = currentSelectionStart + pre_syntax.length + text.length;
    }

    _insertText(pre_syntax, post_syntax, placeholder) {
        this._replaceSelectedText(pre_syntax, post_syntax, this._getSelectedText(placeholder));
    }

    _removeActiveToken(with_inner_text = false) {
        const text = this.textarea_element.value;
        const token = this.md_tokens[this.active_token];
        const token_start = this.active_token_positions.start - token.start.length;
        const token_end = this.active_token_positions.end + token.end.length;

        const inner_text = text.substring(token_start + token.start.length, token_end - token.end.length);

        let inner_text_end = token_end - token.end.length;

        if (token.middle.length > 0) {
            const pos = inner_text.indexOf(token.middle);
            if (pos >= 0) {
                inner_text_end = token_start + token.start.length + pos;
            }
        }

        this.textarea_element.value = text.substring(0, token_start)
            + (with_inner_text ? "" : text.substring(token_start + token.start.length, inner_text_end))
            + text.substring(token_end);

        this.textarea_element.focus();
        this.textarea_element.selectionStart = token_start;
        this.textarea_element.selectionEnd = inner_text_end - token.start.length;
    }

    _createElement(tag, classes = []) {
        return DomCreate.create(tag, classes);
    }

    _createOverlayElement() {
        const overlay_box_element = this._createElement("div", ['box', 'overlay', 'is-hidden']);
        const overlay_content_element = this._createElement("div", ['overlay-content']);
        overlay_box_element.insertBefore(overlay_content_element, null);
        const close_button = this._createElement("button", ['delete']);
        close_button.type = "button";
        overlay_box_element.insertBefore(close_button, null);

        overlay_box_element.addEventListener("click", (event) => {
            overlay_box_element.classList.add('is-hidden');
        });

        this.textarea_element.parentNode.insertBefore(overlay_box_element, null);

        return {
            element: overlay_box_element,
            content: overlay_content_element,
            close: close_button,
        };
    }
}